import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useBusiness } from "./use-business";
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subMonths, subWeeks } from "date-fns";
import { convertCurrencyAmount } from "@/lib/fx-rates";

type MembershipAnalyticsData = {
  // Key metrics
  visitsThisWeek: number;
  visitsThisMonth: number;
  walkInRevenueThisMonth: number;
  invoiceRevenueThisMonth: number; // Invoice revenue
  totalRevenueThisMonth: number; // Total revenue (walk-ins + invoices)
  activeMembers: number;
  expiringSoon: number; // next 7 days
  churned30d: number; // expired/cancelled in last 30 days
  memberVisitsThisMonth: number;
  walkInVisitsThisMonth: number;

  topPlans: Array<{ plan_name: string; active_members: number }>;
  walkInByPaymentMethod: Array<{ method: string; revenue: number; count: number }>;

  // Trends (shared UI expects these)
  weekdayTrendsWeek: Array<{ day: string; count: number }>;
  monthlyTrends6Months: Array<{ month: string; count: number }>;

  // Export rows
  exportVisits: Array<{
    check_in_at: string;
    check_out_at: string;
    customer_name: string;
    kind: string;
    party_size: number;
    membership_code: string;
    ticket_code: string;
  }>;
  exportWalkins: Array<{
    created_at: string;
    customer_name: string;
    ticket_code: string;
    amount: number;
    currency: string;
    party_size: number;
  }>;
};

export function useMembershipAnalytics() {
  const { business } = useBusiness();
  const [data, setData] = useState<MembershipAnalyticsData>({
    visitsThisWeek: 0,
    visitsThisMonth: 0,
    walkInRevenueThisMonth: 0,
    invoiceRevenueThisMonth: 0,
    totalRevenueThisMonth: 0,
    activeMembers: 0,
    expiringSoon: 0,
    churned30d: 0,
    memberVisitsThisMonth: 0,
    walkInVisitsThisMonth: 0,
    topPlans: [],
    walkInByPaymentMethod: [],
    weekdayTrendsWeek: [],
    monthlyTrends6Months: [],
    exportVisits: [],
    exportWalkins: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    if (!business?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const now = new Date();
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      // Pull access sessions (last 6 months for trends; should be fine for most small/medium businesses)
      const trendsStart = subMonths(now, 6);
      const { data: sessions, error: sErr } = await (supabase as any)
        .from("access_sessions")
        .select(
          `
          id,
          business_id,
          check_in_at,
          check_out_at,
          party_size,
          membership:memberships(id,membership_code,plan:membership_plans(name)),
          day_pass:day_passes(id,ticket_code,amount,currency,party_size),
          customer:customers(id,name)
        `,
        )
        .eq("business_id", business.id)
        .gte("check_in_at", trendsStart.toISOString())
        .order("check_in_at", { ascending: true })
        .limit(5000);
      if (sErr) throw sErr;

      const sessionsArr = sessions || [];
      const displayCurrency = business.currency || "GHS";
      const convertRevenue = (amount: number, sourceCurrency?: string | null) =>
        convertCurrencyAmount(amount, sourceCurrency || displayCurrency, displayCurrency);

      const inRange = (dIso: string, start: Date, end: Date) => {
        const t = new Date(dIso).getTime();
        return t >= start.getTime() && t <= end.getTime();
      };

      const visitsThisWeek = sessionsArr.filter((s: any) => s.check_in_at && inRange(s.check_in_at, weekStart, weekEnd)).reduce(
        (sum: number, s: any) => sum + Number(s.party_size || 1),
        0,
      );

      const visitsThisMonth = sessionsArr.filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd)).reduce(
        (sum: number, s: any) => sum + Number(s.party_size || 1),
        0,
      );

      const walkInRevenueThisMonth = sessionsArr
        .filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd))
        .filter((s: any) => !!s.day_pass)
        .reduce((sum: number, s: any) => sum + convertRevenue(Number(s.day_pass?.amount || 0), s.day_pass?.currency), 0);

      // Active members count (simple: memberships with status=active and expires_at >= today)
      const { data: mems, error: mErr } = await (supabase as any)
        .from("memberships")
        .select("id,plan_id,expires_at,status,plan:membership_plans(id,name)")
        .eq("business_id", business.id)
        .limit(5000);
      if (mErr) throw mErr;
      const today = new Date();
      const in7 = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
      const churnFrom = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
      const activeMembers = (mems || []).filter((m: any) => {
        if (String(m.status || "") !== "active") return false;
        if (!m.expires_at) return true;
        return new Date(m.expires_at) >= today;
      }).length;

      const expiringSoon = (mems || []).filter((m: any) => {
        if (String(m.status || "") !== "active") return false;
        if (!m.expires_at) return false;
        const exp = new Date(m.expires_at);
        return exp >= today && exp <= in7;
      }).length;

      const churned30d = (mems || []).filter((m: any) => {
        const st = String(m.status || "");
        const exp = m.expires_at ? new Date(m.expires_at) : null;
        const churnStatus = st === "expired" || st === "cancelled";
        const expiredInWindow = !!exp && exp >= churnFrom && exp < today;
        return churnStatus && expiredInWindow;
      }).length;

      // Top plans by active members
      const planCounts = new Map<string, number>();
      for (const m of mems || []) {
        if (String(m.status || "") !== "active") continue;
        if (m.expires_at && new Date(m.expires_at) < today) continue;
        const name = m.plan?.name || "Plan";
        planCounts.set(name, (planCounts.get(name) || 0) + 1);
      }
      const topPlans = Array.from(planCounts.entries())
        .map(([plan_name, active_members]) => ({ plan_name, active_members }))
        .sort((a, b) => b.active_members - a.active_members)
        .slice(0, 6);

      // Weekday trends (last 8 weeks cumulative)
      const weekdayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
      const weekdayIndex = (d: Date) => (d.getDay() + 6) % 7; // Mon=0..Sun=6
      const cumulativeStart = subWeeks(now, 8);
      const weekdayCounts = new Array(7).fill(0);
      for (const s of sessionsArr) {
        const dt = s.check_in_at ? new Date(s.check_in_at) : null;
        if (!dt) continue;
        if (dt < cumulativeStart) continue;
        weekdayCounts[weekdayIndex(dt)] += Number(s.party_size || 1);
      }
      const weekdayTrendsWeek = weekdayLabels.map((day, i) => ({ day, count: weekdayCounts[i] }));

      // Monthly trends (last 6 months)
      const months: Array<{ start: Date; key: string }> = [];
      for (let i = 5; i >= 0; i--) {
        const d = subMonths(now, i);
        months.push({ start: startOfMonth(d), key: format(d, "MMM") });
      }
      const monthlyCounts = months.map(() => 0);
      for (const s of sessionsArr) {
        const dt = s.check_in_at ? new Date(s.check_in_at) : null;
        if (!dt) continue;
        for (let i = 0; i < months.length; i++) {
          const start = months[i].start;
          const end = endOfMonth(start);
          if (dt >= start && dt <= end) {
            monthlyCounts[i] += Number(s.party_size || 1);
            break;
          }
        }
      }
      const monthlyTrends6Months = months.map((m, i) => ({ month: m.key, count: monthlyCounts[i] }));

      // Exports
      const exportVisits = sessionsArr
        .filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd))
        .map((s: any) => ({
          check_in_at: s.check_in_at || "",
          check_out_at: s.check_out_at || "",
          customer_name: s.customer?.name || "",
          kind: s.membership ? "member" : "walk_in",
          party_size: Number(s.party_size || 1),
          membership_code: s.membership?.membership_code || "",
          ticket_code: s.day_pass?.ticket_code || "",
        }));

      const exportWalkins = sessionsArr
        .filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd))
        .filter((s: any) => !!s.day_pass)
        .map((s: any) => ({
          created_at: s.day_pass?.created_at || s.check_in_at || "",
          customer_name: s.customer?.name || "",
          ticket_code: s.day_pass?.ticket_code || "",
          amount: Number(s.day_pass?.amount || 0),
          currency: String(s.day_pass?.currency || (business as any)?.currency || "GHS"),
          party_size: Number(s.day_pass?.party_size || s.party_size || 1),
        }));

      // Member vs walk-in visits this month (party-size aware)
      const memberVisitsThisMonth = sessionsArr
        .filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd))
        .filter((s: any) => !!s.membership)
        .reduce((sum: number, s: any) => sum + Number(s.party_size || 1), 0);
      const walkInVisitsThisMonth = sessionsArr
        .filter((s: any) => s.check_in_at && inRange(s.check_in_at, monthStart, monthEnd))
        .filter((s: any) => !!s.day_pass)
        .reduce((sum: number, s: any) => sum + Number(s.party_size || 1), 0);

      // Walk-in revenue by payment method (month)
      const byMethod = new Map<string, { revenue: number; count: number }>();
      for (const s of sessionsArr) {
        if (!s.check_in_at || !inRange(s.check_in_at, monthStart, monthEnd)) continue;
        if (!s.day_pass) continue;
        const method = String(s.day_pass?.payment_method || "cash");
        const cur = byMethod.get(method) || { revenue: 0, count: 0 };
        cur.revenue += convertRevenue(Number(s.day_pass?.amount || 0), s.day_pass?.currency);
        cur.count += 1;
        byMethod.set(method, cur);
      }
      const walkInByPaymentMethod = Array.from(byMethod.entries())
        .map(([method, v]) => ({ method, revenue: v.revenue, count: v.count }))
        .sort((a, b) => b.revenue - a.revenue);

      // Fetch invoice revenue (paid invoices this month)
      const { data: invoices, error: invError } = await (supabase as any)
        .from("invoices")
        .select(`
          id,
          currency,
          invoice_items(id, quantity, unit_price),
          invoice_adjustments(id, type, mode, amount, percent_rate, included_in_prices)
        `)
        .eq("business_id", business.id)
        .eq("status", "paid")
        .gte("paid_at", monthStart.toISOString())
        .lte("paid_at", monthEnd.toISOString());

      if (invError) throw invError;

      // Calculate invoice revenue (items + adjustments)
      let invoiceRevenueThisMonth = 0;
      if (invoices) {
        for (const inv of invoices) {
          // Sum line items
          const itemsTotal = (inv.invoice_items || []).reduce(
            (sum: number, item: any) => sum + Number(item.quantity || 0) * Number(item.unit_price || 0),
            0
          );

          // Apply adjustments
          let adjustedTotal = itemsTotal;
          for (const adj of inv.invoice_adjustments || []) {
            if (adj.type === "tax" && adj.included_in_prices) {
              // Tax is already included, don't add it
              continue;
            }
            if (adj.mode === "percent") {
              const percentValue = Number(adj.percent_rate || 0);
              if (adj.type === "tax") {
                adjustedTotal += (itemsTotal * percentValue) / 100;
              } else if (adj.type === "discount") {
                adjustedTotal -= (itemsTotal * percentValue) / 100;
              }
            } else {
              // Fixed amount
              if (adj.type === "tax") {
                adjustedTotal += Number(adj.amount || 0);
              } else if (adj.type === "discount") {
                adjustedTotal -= Number(adj.amount || 0);
              }
            }
          }

          invoiceRevenueThisMonth += convertRevenue(adjustedTotal, (inv as any).currency);
        }
      }

      // Calculate total revenue
      const totalRevenueThisMonth = walkInRevenueThisMonth + invoiceRevenueThisMonth;

      setData({
        visitsThisWeek,
        visitsThisMonth,
        walkInRevenueThisMonth,
        invoiceRevenueThisMonth,
        totalRevenueThisMonth,
        activeMembers,
        expiringSoon,
        churned30d,
        memberVisitsThisMonth,
        walkInVisitsThisMonth,
        topPlans,
        walkInByPaymentMethod,
        weekdayTrendsWeek,
        monthlyTrends6Months,
        exportVisits,
        exportWalkins,
      });
    } catch (e: any) {
      setError(e?.message || "Failed to load membership analytics");
    } finally {
      setLoading(false);
    }
  }, [business?.currency, business?.id]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { data, loading, error };
}


